import { FormControl, MenuItem, Select } from '@material-ui/core'
import { AccountSettingSelect } from 'components/controls/misc'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import PowertoolsLogoName from 'img/powertools-logo-name.svg'
import PtGearLogo from 'img/pt_gear_logo.svg'
import { editUserAttribute } from 'lib/api'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_CURRENCIES } from 'shared'
import { APP_LANGUAGES } from 'utils/constants'

export interface OnboardingWelcomeProps {
  handleNext: () => void
}

export const Welcome = ({ handleNext }: OnboardingWelcomeProps): JSX.Element => {
  const [showWelcomeText, setShowWelcomeText] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState('English')
  const [selectedCurrency, setSelectedCurrency] = useState('')
  const { t, i18n } = useTranslation()
  const { fetchUser } = useUser()

  const handleSelectCurrency = (event: React.ChangeEvent<{ value: unknown }>) => {
    if (event.target.value) {
      const currency = event.target.value as string
      setSelectedCurrency(currency)
      editUserAttribute(currency, 'currency')
    }
  }

  useEffect(() => {
    if (selectedCurrency && selectedLanguage) {
      setShowWelcomeText(true)
    }
  }, [selectedCurrency, selectedLanguage])

  const handleStart = async () => {
    await fetchUser()
    handleNext()
  }

  return (
    <>
      <div className="onboarding-container">
        <div className="container">
          <div className="d-flex flex-wrap align-items-end column-gap-2 mt-lg-2 mt-3">
            <h2 className="fs-1 fw-semibold p-0 m-0">{t('onboarding.welcome')}</h2>{' '}
            <img src={PowertoolsLogoName} alt="powertools-logo" className="powertools-name" />
          </div>
          <div className="row h-100">
            <div className="col-xl-6 d-flex justify-content-center">
              <img src={PtGearLogo} className="onboarding-pt-logo d-none d-xl-block" />
            </div>
            <div className="col-xl-6">
              <div className="onboarding-content d-flex flex-column py-4 p-md-5">
                <div style={{ width: '200px' }} className="mt-4">
                  <p>{t('onboarding.selectLanguage')}</p>
                  <AccountSettingSelect
                    attribute={'language'}
                    options={APP_LANGUAGES}
                    onChange={(language) => {
                      i18n.changeLanguage(language as string)
                      setSelectedLanguage(language as string)
                    }}
                  />
                  <p className="mt-4">
                    <p>{t('onboarding.selectCurrency')}</p>
                  </p>
                  <FormControl fullWidth>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={selectedCurrency}
                      label="Age"
                      onChange={handleSelectCurrency}
                      data-testid="select-currency"
                    >
                      {SUPPORTED_CURRENCIES.map((currency) => (
                        <MenuItem key={currency} value={currency}>
                          {currency}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                {showWelcomeText && (
                  <div className="pb-sm-10fw-light">
                    <p className="m-0 p-0 fs-5 mt-5">{t('onboarding.description')}</p>
                    <div className="d-flex flex-row align-items-center pt-1">
                      <i className="fa-solid fa-diamond pe-1"></i>{' '}
                      <p className="m-0 p-0 fs-5">{t('onboarding.descriptionOne')}</p>
                    </div>
                    <div className="d-flex flex-row align-items-center pt-1">
                      <i className="fa-solid fa-diamond pe-1"></i>{' '}
                      <p className="m-0 p-0 fs-5">{t('onboarding.descriptionTwo')}</p>
                    </div>
                    <div className="d-flex flex-row align-items-center pt-1">
                      <i className="fa-solid fa-diamond pe-1"></i>{' '}
                      <p className="m-0 p-0 fs-5">{t('onboarding.descriptionThree')}</p>
                    </div>
                    <div className="d-flex flex-row align-items-center pt-1">
                      <i className="fa-solid fa-diamond pe-1 mb-4"></i>{' '}
                      <p className="m-0 p-0 fs-5">{t('onboarding.descriptionFour')}</p>
                    </div>
                    <div className="d-flex flex-row align-items-center pt-1">
                      <i className="fa-solid fa-diamond pe-1"></i>{' '}
                      <p className="m-0 p-0 fs-5">{t('onboarding.descriptionFive')}</p>
                    </div>
                    <div className="d-flex flex-row align-items-center pt-1">
                      <i className="fa-solid fa-diamond pe-1"></i>{' '}
                      <p className="m-0 p-0 fs-5">{t('onboarding.descriptionSix')}</p>
                    </div>
                    <div className="pt-4 ms-4" style={{ zIndex: '1600', position: 'relative' }}>
                      <Button
                        color="primary"
                        variant="contained"
                        scaleUp={true}
                        style={{ width: '230px' }}
                        onClick={handleStart}
                        data-testid="start-onboarding"
                      >
                        {t('onboarding.ready')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
